import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ChevronDown, Info, Pause, Play } from 'lucide-react';
import { Botao, classesBotao } from '../../componentes/base';
import { BuscaRemota, CampoData, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { useToast } from '../../componentes/toast';
import { get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomeParceiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { TIPOS_ATIVIDADE, type Motivo, type TipoAtividade } from './tipos';
import { baixarTexto, ConfirmarPorContagem, Estrelas, iniciais } from './CrmComponentes';
import { csv, dataDaSugestao } from './crm-sugestoes';
import { caminhoDaNegociacao, caminhoDoParceiro } from './crm-caminhos';
import type { EtapaCrm, NegociacaoCrm } from './ComercialFunil';
import { FalhaNoBloco, MOTIVO_PAUSA_MAX, MOTIVO_PAUSA_MIN, motivoDaPausa, porBlocos } from '@malgoos/shared';

/**
 * CRM no novo desenho, lote 3 (C-A): a lista do Comercial (a do clone do RD), com seleção, "selecionar todos deste filtro",
 * paginação (10, 25, 50, 100) e as ações em massa: Transferir, Alterar status, Adicionar ou alterar (qualificação, campanha,
 * fonte, produto), Criar tarefa, Mover, Exportar e Excluir. Toda ação que muda dado pede a confirmação pela contagem.
 */

export const TAMANHOS = [10, 25, 50, 100] as const;
const LIMITE = 500;
/** Exportação: páginas de 200 até o fim da seleção (laço por página; nunca para no primeiro lote). */
const POR_PAGINA = 200;

type Operacao =
  | { fazer: 'transferir'; responsavel_id: string }
  | { fazer: 'status'; status: 'andamento' | 'pausado' | 'vendido' | 'perdido'; motivo_perda_id?: string; motivo?: string }
  | { fazer: 'qualificacao'; qualificacao: number }
  | { fazer: 'fonte' | 'campanha'; valor: string }
  | { fazer: 'produto'; item: { servico_id: string; descricao: string; quantidade: number; preco_centavos: number; recorrencia: string } }
  | { fazer: 'tarefa'; titulo: string; tipo: TipoAtividade; data_hora: string }
  | { fazer: 'mover'; etapa_id: string }
  | { fazer: 'excluir' };

interface Pedido {
  titulo: string;
  texto: ReactNode;
  pergunta: string;
  botao: string;
  /** Monta a operação com o que a pessoa escolheu no corpo; null mantém o botão travado. */
  operacao: (v: Record<string, string>) => Operacao | null;
  corpo?: (mudar: (v: Record<string, string>) => void, v: Record<string, string>) => ReactNode;
}

export function ListaComercial({
  total,
  itens,
  pagina,
  tamanho,
  aoPagina,
  aoTamanho,
  etapas,
  paramsFiltro,
  carregando,
  aoSobre,
}: {
  total: number;
  itens: NegociacaoCrm[];
  pagina: number;
  tamanho: number;
  aoPagina: (p: number) => void;
  aoTamanho: (n: number) => void;
  etapas: EtapaCrm[];
  paramsFiltro: Record<string, unknown>;
  carregando: boolean;
  /** Ajustes 27/10 (K17, K18): o ícone "i" da linha abre o "Sobre a negociação". */
  aoSobre?: (n: NegociacaoCrm) => void;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const chaveFiltro = JSON.stringify(paramsFiltro);
  const [sel, setSel] = useState<{ chave: string; ids: Set<string> }>({ chave: '', ids: new Set() });
  const marcados = sel.chave === chaveFiltro ? sel.ids : new Set<string>();
  const mudarSel = (f: (x: Set<string>) => Set<string>) => setSel((a) => ({ chave: chaveFiltro, ids: f(a.chave === chaveFiltro ? a.ids : new Set()) }));
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [executando, setExecutando] = useState(false);
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
  const motivos = useQuery({ queryKey: ['pipeline', 'motivos'], queryFn: () => get<{ itens: Motivo[] }>('/pipeline/motivos-perda') });
  // Val5 (29/10): a ação em massa pede `pipeline.em_massa` (e a ação de cada uma: editar, criar tarefa, marcar venda ou perda,
  // excluir); exportar pede `pipeline.exportar`. A seleção aparece para quem faz uma das duas.
  const emMassa = s.pode('pipeline.em_massa');
  const podeEditar = emMassa && s.pode('pipeline.editar');
  const podeExportar = s.pode('pipeline.exportar');
  const podeSelecionar = emMassa || podeExportar;
  const ids = [...marcados];
  const n = ids.length;
  const paginas = Math.max(1, Math.ceil(total / tamanho));
  const todasDaPagina = itens.length > 0 && itens.every((x) => marcados.has(x.id));
  const nNeg = t('crm.comercial.nNegociacoes', { count: n });

  const pedir = (p: Pedido, inicial: Record<string, string> = {}) => {
    setValores(inicial);
    setPedido(p);
  };
  const executar = async () => {
    const operacao = pedido?.operacao(valores);
    if (!operacao) return;
    setExecutando(true);
    try {
      // Rodada crm1 (ROB-M1): o servidor aceita até 50 por chamada; a seleção vai em blocos e o resultado é somado.
      const r = { alterados: 0, ignorados: 0, erros: [] as { mensagem: string }[] };
      const partes = await porBlocos(ids, (bloco) => post<{ alterados: number; ignorados: number; erros: { mensagem: string }[] }>('/pipeline/crm/lote', { ids: bloco, operacao }));
      for (const p of partes) {
        r.alterados += p.alterados;
        r.ignorados += p.ignorados;
        r.erros.push(...p.erros);
      }
      toast.sucesso(t('crm.comercial.resultado', { alterados: r.alterados, ignorados: r.ignorados }));
      if (r.erros.length) toast.erro(r.erros[0]!.mensagem);
      setPedido(null);
      mudarSel(() => new Set());
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
    } catch (e) {
      // Rodada crm2 (ROB-M1): blocos já gravados saem da seleção; repetir manda só o que faltou.
      if (e instanceof FalhaNoBloco) {
        const feitos = new Set(e.feitos as string[]);
        mudarSel((s) => new Set([...s].filter((x) => !feitos.has(x))));
        setPedido(null);
        await qc.invalidateQueries({ queryKey: ['pipeline'] });
        toast.erro(t('crm.contagem.parcial', { feitos: e.feitos.length, total: e.total, erro: mensagemDeErro(e.causa) }));
      } else toast.erro(mensagemDeErro(e));
    } finally {
      setExecutando(false);
    }
  };
  const selVal = (k: string) => (mudar: (v: Record<string, string>) => void, v: Record<string, string>, opcoes: { valor: string; rotulo: string }[], rotulo: string) => (
    <Rotulo id={`${id}-${k}`} rotulo={rotulo} obrigatorio>
      <Selecao id={`${id}-${k}`} data-lote-campo={k} value={v[k] ?? ''} vazio={t('crm.form.selecionar')} opcoes={opcoes} onChange={(e) => mudar({ [k]: e.target.value })} />
    </Rotulo>
  );

  const transferir: ItemMenu[] = (membros.data?.itens ?? []).map((m) => ({
    chave: `t-${m.id}`,
    rotulo: nomePessoa(m.nome),
    aoClicar: () =>
      pedir({
        titulo: t('crm.comercial.lote.transferirTitulo'),
        texto: t('crm.comercial.lote.transferirTexto', { n: nNeg, nome: nomePessoa(m.nome) }),
        pergunta: t('crm.comercial.lote.perguntaTransferir'),
        botao: t('crm.comercial.lote.transferirBotao'),
        operacao: () => ({ fazer: 'transferir', responsavel_id: m.id }),
      }),
  }));
  const status: ItemMenu[] = (['andamento', 'perdido', 'pausado', 'vendido'] as const).map((st) => ({
    chave: `s-${st}`,
    rotulo: t(`crm.comercial.statusNeg.${st}`),
    aoClicar: () =>
      pedir({
        titulo: t('crm.comercial.lote.statusTitulo'),
        texto: t('crm.comercial.lote.statusTexto', { n: nNeg, status: t(`crm.comercial.statusNeg.${st}`) }),
        pergunta: t('crm.comercial.lote.perguntaAlterar'),
        botao: t('crm.comercial.lote.statusBotao'),
        // Item 9 da lista de 30/10 (B): pausar e retomar em massa levam a justificativa (a regra é a `motivoDaPausa`, a mesma da API).
        operacao: (v) => {
          if (st === 'perdido') return v.motivo ? { fazer: 'status', status: st, motivo_perda_id: v.motivo } : null;
          if (st === 'pausado' || st === 'andamento') {
            const r = motivoDaPausa(v.justificativa);
            return r.ok ? { fazer: 'status', status: st, motivo: r.valor } : null;
          }
          return { fazer: 'status', status: st };
        },
        corpo:
          st === 'perdido'
            ? (m, v) => selVal('motivo')(m, v, (motivos.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome })), t('campos.motivo'))
            : st === 'pausado' || st === 'andamento'
              ? (m, v) => (
                  <Rotulo id={`${id}-justificativa`} rotulo={t('crm.pausa.motivo')} obrigatorio ajuda={t('crm.pausa.erroMotivo', { min: MOTIVO_PAUSA_MIN, max: MOTIVO_PAUSA_MAX })}>
                    <EntradaSensivel mascara={null} livre id={`${id}-justificativa`} data-lote-campo="justificativa" maxLength={MOTIVO_PAUSA_MAX} value={v.justificativa ?? ''} onChange={(e) => m({ justificativa: e.target.value })} />
                  </Rotulo>
                )
              : undefined,
      }),
  }));
  // Val5 (29/10): venda e perda pedem `pipeline.ganhar_perder`; andamento e pausa, `pipeline.editar`.
  const statusPermitidos = status.filter((x) => (x.chave === 's-vendido' || x.chave === 's-perdido' ? s.pode('pipeline.ganhar_perder') : podeEditar));
  const campo: ItemMenu[] = [
    {
      chave: 'c-qualificacao',
      rotulo: t('crm.comercial.qualificacao'),
      aoClicar: () =>
        pedir({
          titulo: t('crm.comercial.lote.campoTitulo', { campo: t('crm.comercial.qualificacao').toLowerCase() }),
          texto: t('crm.comercial.lote.campoTexto', { n: nNeg, campo: t('crm.comercial.qualificacao').toLowerCase() }),
          pergunta: t('crm.comercial.lote.perguntaAlterar'),
          botao: t('crm.comercial.lote.campoBotao'),
          operacao: (v) => (v.qualificacao ? { fazer: 'qualificacao', qualificacao: Number(v.qualificacao) } : null),
          corpo: (m, v) => selVal('qualificacao')(m, v, [1, 2, 3, 4, 5].map((x) => ({ valor: String(x), rotulo: t('crm.comercial.estrelas', { count: x }) })), t('crm.comercial.qualificacao')),
        }),
    },
    ...(['campanha', 'fonte'] as const).map((k) => ({
      chave: `c-${k}`,
      rotulo: t(`crm.filtros.${k}`),
      aoClicar: () =>
        pedir({
          titulo: t('crm.comercial.lote.campoTitulo', { campo: t(`crm.filtros.${k}`).toLowerCase() }),
          texto: t('crm.comercial.lote.campoTexto', { n: nNeg, campo: t(`crm.filtros.${k}`).toLowerCase() }),
          pergunta: t('crm.comercial.lote.perguntaAlterar'),
          botao: t('crm.comercial.lote.campoBotao'),
          operacao: (v) => (v[k]?.trim() ? { fazer: k, valor: v[k]!.trim() } : null),
          corpo: (m, v) => (
            <Rotulo id={`${id}-${k}`} rotulo={t(`crm.filtros.${k}`)} obrigatorio>
              <Entrada id={`${id}-${k}`} data-lote-campo={k} maxLength={120} value={v[k] ?? ''} onChange={(e) => m({ [k]: e.target.value })} />
            </Rotulo>
          ),
        }),
    })),
    {
      chave: 'c-produto',
      rotulo: t('crm.filtros.produto'),
      aoClicar: () =>
        pedir({
          titulo: t('crm.comercial.lote.campoTitulo', { campo: t('crm.filtros.produto').toLowerCase() }),
          texto: t('crm.comercial.lote.produtoTexto', { n: nNeg }),
          pergunta: t('crm.comercial.lote.perguntaAlterar'),
          botao: t('crm.comercial.lote.campoBotao'),
          operacao: (v) =>
            v.servico ? { fazer: 'produto', item: { servico_id: v.servico, descricao: v.servico_nome ?? '', quantidade: 1, preco_centavos: Number(v.preco ?? 0), recorrencia: v.recorrencia || 'mensal' } } : null,
          corpo: (m, v) => (
            <Rotulo id={`${id}-servico`} rotulo={t('crm.filtros.produto')} obrigatorio>
              <BuscaRemota
                id={`${id}-servico`}
                def={{ caminho: '/cadastros/servicos', rotulo: (i) => String(i.nome) }}
                valor={v.servico || null}
                rotuloAtual={v.servico_nome}
                aoMudar={(x, item) => m({ servico: x ?? '', servico_nome: item ? String(item.nome) : '', preco: item ? String(item.preco_centavos ?? item.valor_centavos ?? 0) : '', recorrencia: item?.recorrencia ? String(item.recorrencia) : 'mensal' })}
              />
            </Rotulo>
          ),
        }),
    },
  ];
  const tarefa = () =>
    pedir({
      titulo: t('crm.comercial.lote.tarefaTitulo'),
      texto: t('crm.comercial.lote.tarefaTexto', { n: nNeg }),
      pergunta: t('crm.comercial.lote.perguntaCriar'),
      botao: t('crm.comercial.lote.tarefaBotao'),
      operacao: (v) => (v.titulo?.trim() && v.data ? { fazer: 'tarefa', titulo: v.titulo.trim(), tipo: (v.tipo || 'ligacao') as TipoAtividade, data_hora: new Date(v.data).toISOString() } : null),
      corpo: (m, v) => (
        <div className="flex flex-col gap-3">
          <Rotulo id={`${id}-tt`} rotulo={t('crm.form.assunto')} obrigatorio>
            <EntradaSensivel mascara={null} livre id={`${id}-tt`} data-lote-campo="titulo" maxLength={200} value={v.titulo ?? ''} onChange={(e) => m({ titulo: e.target.value })} />
          </Rotulo>
          <div className="grid gap-3 sm:grid-cols-2">
            <Rotulo id={`${id}-tp`} rotulo={t('crm.form.tipo')}>
              <Selecao id={`${id}-tp`} value={v.tipo || 'ligacao'} opcoes={TIPOS_ATIVIDADE.map((x) => ({ valor: x, rotulo: t(`tipoAtividade.${x}`) }))} onChange={(e) => m({ tipo: e.target.value })} />
            </Rotulo>
            <Rotulo id={`${id}-td`} rotulo={t('crm.form.dataHora')} obrigatorio>
              <CampoData id={`${id}-td`} data-lote-campo="data" type="datetime-local" value={v.data ?? ''} onChange={(e) => m({ data: e.target.value })} />
            </Rotulo>
          </div>
        </div>
      ),
    }, { data: dataDaSugestao(1), tipo: 'ligacao' });
  const mover = () =>
    pedir({
      titulo: t('crm.comercial.lote.moverTitulo'),
      texto: t('crm.comercial.lote.moverTexto', { n: nNeg }),
      pergunta: t('crm.comercial.lote.perguntaAlterar'),
      botao: t('crm.comercial.lote.moverBotao'),
      operacao: (v) => (v.etapa ? { fazer: 'mover', etapa_id: v.etapa } : null),
      corpo: (m, v) => selVal('etapa')(m, v, etapas.filter((e) => e.tipo === 'aberta').map((e) => ({ valor: e.id, rotulo: e.nome })), t('crm.comercial.etapa')),
    });
  const excluir = () =>
    pedir({
      titulo: t('crm.comercial.lote.excluirTitulo'),
      texto: t('crm.comercial.lote.excluirTexto', { n: nNeg }),
      pergunta: t('crm.comercial.lote.perguntaExcluir'),
      botao: t('crm.comercial.lote.excluirBotao'),
      operacao: () => ({ fazer: 'excluir' }),
    });
  const exportar = async () => {
    try {
      const linhas: NegociacaoCrm[] = [];
      for (let p = 1; p <= Math.ceil(Math.min(total, LIMITE) / POR_PAGINA); p++) linhas.push(...(await get<{ itens: NegociacaoCrm[] }>('/pipeline/crm/negociacoes', { ...paramsFiltro, pagina: p, limite: POR_PAGINA })).itens);
      const escolhidas = linhas.filter((x) => marcados.has(x.id));
      const c = t('crm.comercial.exportCab', { returnObjects: true }) as string[];
      baixarTexto(
        t('crm.comercial.arquivoCsv'),
        csv([
          c,
          ...escolhidas.map((x) => [x.titulo, x.parceiro_nome, x.etapa_nome, t(`crm.comercial.statusNeg.${x.status}`), x.responsavel_nome, x.qualificacao, x.utm_fonte, x.utm_campanha, dinheiro(x.valor_unico_centavos, x.moeda), dinheiro(x.valor_recorrente_centavos, x.moeda), dataHora(x.created_at), x.previsao_fechamento]),
        ]),
      );
      toast.sucesso(t('crm.comercial.exportadas', { count: escolhidas.length }));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  const menuLote = (rotulo: string, itensMenu: ItemMenu[]) => <Menu rotulo={rotulo} itens={itensMenu} classeGatilho={classesBotao('secundario', 'sm')} gatilho={<>{rotulo}<ChevronDown className="size-4" aria-hidden /></>} />;

  const icone = (st: NegociacaoCrm['status']) =>
    st === 'vendido' ? <ArrowUp className="size-4 text-verde" aria-hidden /> : st === 'perdido' ? <ArrowDown className="size-4 text-perigo" aria-hidden /> : st === 'pausado' ? <Pause className="size-4 text-tinta-3" aria-hidden /> : <Play className="size-4 text-azul" aria-hidden />;

  return (
    <div className="flex flex-col gap-3" data-lista-comercial="">
      {podeSelecionar && marcados.size > 0 && (
        <section aria-label={t('crm.atividades.acoesMassa')} className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-borda bg-sup-1 p-3" data-barra-selecao="">
          <p className="min-w-0 flex-1 basis-48 text-sm font-medium">
            {t('crm.comercial.selecionadas', { count: marcados.size })}{' '}
            {total > marcados.size && (
              <button
                type="button"
                data-selecionar-todos=""
                className="font-medium text-acento-forte underline"
                onClick={() =>
                  void get<{ ids: string[] }>('/pipeline/crm/negociacoes/selecao', paramsFiltro)
                    .then((r) => mudarSel(() => new Set(r.ids)))
                    .catch((e) => toast.erro(mensagemDeErro(e)))
                }
              >
                {total > LIMITE ? t('crm.atividades.selecionarTodosLimite', { count: total, limite: LIMITE }) : t('crm.atividades.selecionarTodos', { count: total })}
              </button>
            )}
          </p>
          <Botao variante="fantasma" tamanho="sm" onClick={() => mudarSel(() => new Set())}>
            {t('crm.atividades.limparSelecao')}
          </Botao>
          {podeEditar && menuLote(t('crm.comercial.lote.transferir'), transferir)}
          {emMassa && (podeEditar || s.pode('pipeline.ganhar_perder')) && menuLote(t('crm.comercial.lote.status'), statusPermitidos)}
          {podeEditar && menuLote(t('crm.comercial.lote.campo'), campo)}
          {emMassa && s.pode('pipeline.criar') && (
            <Botao tamanho="sm" onClick={tarefa} data-lote-tarefa="">
              {t('crm.comercial.lote.tarefa')}
            </Botao>
          )}
          {podeEditar && (
            <Botao tamanho="sm" onClick={mover} data-lote-mover="">
              {t('crm.comercial.lote.mover')}
            </Botao>
          )}
          {podeExportar && (
            <Botao tamanho="sm" onClick={() => void exportar()} data-exportar="">
              {t('crm.atividades.exportar')}
            </Botao>
          )}
          {emMassa && s.pode('pipeline.excluir') && (
            <Botao tamanho="sm" variante="perigo" onClick={excluir} data-lote-excluir="">
              {t('crm.comercial.lote.excluir')}
            </Botao>
          )}
        </section>
      )}
      <div className="overflow-x-auto rounded-xl border border-borda bg-sup-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borda bg-sup-2 text-left">
              {podeSelecionar && (
                <th scope="col" className="w-10 px-3 py-1.5">
                  <input
                    type="checkbox"
                    aria-label={t('crm.comercial.selecionarPagina')}
                    checked={todasDaPagina}
                    onChange={(e) =>
                      mudarSel((x) => {
                        const nx = new Set(x);
                        for (const l of itens) {
                          if (e.target.checked) nx.add(l.id);
                          else nx.delete(l.id);
                        }
                        return nx;
                      })
                    }
                  />
                </th>
              )}
              {(['negociacao', 'responsavel', 'qualificacao', 'etapa', 'valor', 'criacao', 'status'] as const).map((c) => (
                <th key={c} scope="col" className="h-11 px-4 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-tinta-3 uppercase">
                  {t(`crm.comercial.colunas.${c}`)}
                </th>
              ))}
              {aoSobre && (
                <th scope="col" className="relative w-12 px-2 py-2">
                  <span className="sr-only">{t('crm.comercial.sobre')}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {itens.map((x) => (
              <tr key={x.id} className={`border-t border-borda ${marcados.has(x.id) ? 'bg-sup-2' : ''}`} data-linha-negociacao={x.id}>
                {podeSelecionar && (
                  <td className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label={t('crm.atividades.selecionar', { nome: textoLivre(x.titulo, x.parceiro_nome) })}
                      checked={marcados.has(x.id)}
                      onChange={(e) =>
                        mudarSel((m) => {
                          const nx = new Set(m);
                          if (e.target.checked) nx.add(x.id);
                          else nx.delete(x.id);
                          return nx;
                        })
                      }
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <Link to={caminhoDaNegociacao(x.id)} className="font-medium text-acento-forte hover:underline">
                    {textoLivre(x.titulo, x.parceiro_nome)}
                  </Link>
                  {/* L12 (28/10): como no clone, o nome da empresa abaixo da negociação leva à ficha dela (com acesso a Cadastros); sem
                      empresa, o cliente é o contato e o link vai à ficha do contato (rcrm4 SPEC-B2). */}
                  {x.parceiro_nome &&
                    (s.pode('cadastros.ver') ? (
                      <Link to={caminhoDoParceiro(x.parceiro_id, x.parceiro_tipo)} className="block text-xs text-tinta-2 hover:text-acento-forte hover:underline" data-empresa-da-negociacao="">
                        {nomeParceiro(x.parceiro_nome)}
                      </Link>
                    ) : (
                      <span className="block text-xs text-tinta-3">{nomeParceiro(x.parceiro_nome)}</span>
                    ))}
                </td>
                <td className="px-4 py-3">
                  {x.responsavel_nome && (
                    <span title={nomePessoa(x.responsavel_nome)} aria-label={nomePessoa(x.responsavel_nome)} className="flex size-7 items-center justify-center rounded-full bg-sup-3 text-xs font-semibold text-tinta-2">
                      {iniciais(x.responsavel_nome)}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Estrelas valor={x.qualificacao} rotulo={t('crm.comercial.estrelas', { count: x.qualificacao })} />
                </td>
                <td className="px-4 py-3">{x.etapa_nome}</td>
                <td className="numero px-4 py-3 whitespace-nowrap">
                  {/* K17: como no clone, o valor leva aos produtos da negociação; sem valor, "Adicionar valor". */}
                  {x.itens ? (
                    <Link to={`${caminhoDaNegociacao(x.id)}?aba=produtos`} className="text-acento-forte hover:underline">
                      <span className="block">{t('crm.comercial.unico', { valor: dinheiro(x.valor_unico_centavos, x.moeda) })}</span>
                      <span className="block">{t('crm.comercial.recorrente', { valor: dinheiro(x.valor_recorrente_centavos, x.moeda) })}</span>
                    </Link>
                  ) : x.valor_centavos ? (
                    dinheiro(x.valor_centavos, x.moeda)
                  ) : (
                    <Link to={`${caminhoDaNegociacao(x.id)}?aba=produtos`} className="font-sans text-xs text-acento-forte hover:underline" data-adicionar-valor="">
                      {t('crm.comercial.adicionarValor')}
                    </Link>
                  )}
                </td>
                <td className="numero px-4 py-3 whitespace-nowrap">{data(x.created_at)}</td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1.5" title={t(`crm.comercial.statusNeg.${x.status}`)}>
                    {icone(x.status)}
                    <span className="text-xs">{t(`crm.comercial.statusNeg.${x.status}`)}</span>
                  </span>
                </td>
                {aoSobre && (
                  <td className="px-2 py-3">
                    <button
                      type="button"
                      data-sobre-negociacao={x.id}
                      aria-label={t('crm.comercial.sobreDe', { nome: textoLivre(x.titulo, x.parceiro_nome) })}
                      title={t('crm.comercial.sobre')}
                      onClick={() => aoSobre(x)}
                      className="flex size-9 items-center justify-center rounded text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
                    >
                      <Info className="size-4" aria-hidden />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!itens.length && (
              <tr>
                <td colSpan={aoSobre ? 9 : 8} className="px-4 py-6 text-center text-tinta-3">
                  {carregando ? tn('comum.carregando') : t('crm.comercial.vazio')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm" data-paginacao="">
        <label className="flex items-center gap-2">
          {t('crm.comercial.exibindo')}
          <span className="w-20">
            <Selecao aria-label={t('crm.comercial.porPagina')} value={String(tamanho)} opcoes={TAMANHOS.map((x) => ({ valor: String(x), rotulo: String(x) }))} onChange={(e) => aoTamanho(Number(e.target.value))} />
          </span>
          {t('crm.comercial.deTotal', { total })}
        </label>
        <span className="flex items-center gap-2">
          <Botao tamanho="sm" disabled={pagina <= 1} onClick={() => aoPagina(pagina - 1)}>
            {t('crm.comercial.anterior')}
          </Botao>
          <span className="numero font-semibold">
            {pagina} / {paginas}
          </span>
          <Botao tamanho="sm" disabled={pagina >= paginas} onClick={() => aoPagina(pagina + 1)}>
            {t('crm.comercial.proxima')}
          </Botao>
        </span>
      </div>
      {pedido && (
        <ConfirmarPorContagem
          aberto
          titulo={pedido.titulo}
          texto={pedido.texto}
          pergunta={pedido.pergunta}
          botao={pedido.botao}
          n={n}
          corpo={pedido.corpo?.((v) => setValores((x) => ({ ...x, ...v })), valores)}
          podeConfirmar={!!pedido.operacao(valores)}
          carregando={executando}
          aoFechar={() => setPedido(null)}
          aoConfirmar={() => void executar()}
        />
      )}
    </div>
  );
}
